import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import homeRastraPrernaVideo1 from '../../assets/homeRastraPrernaVideo.mp4';
import homeRastraPrernaVideo2 from '../../assets/homeRastraPrernaVideo2.mp4';
import homeRastraPrernaVideo3 from '../../assets/homeRastraPrernaVideo3.mp4';

const rastraVideos = [
  {
    id: 'rastra-3',
    src: homeRastraPrernaVideo3,
    label: 'Rastra Prerna Himmatwaali video 3',
  },
  {
    id: 'rastra-2',
    src: homeRastraPrernaVideo2,
    label: 'Rastra Prerna Himmatwaali video 2',
  },
  {
    id: 'rastra-1',
    src: homeRastraPrernaVideo1,
    label: 'Rastra Prerna Himmatwaali video 1',
  },
];

const DESKTOP_VISIBLE = 3;

function useVisibleSlideCount() {
  const [count, setCount] = useState(1);

  useEffect(() => {
    const media = window.matchMedia('(min-width: 1024px)');
    const sync = () => setCount(media.matches ? DESKTOP_VISIBLE : 1);
    sync();
    media.addEventListener('change', sync);
    return () => media.removeEventListener('change', sync);
  }, []);

  return count;
}

const RastraVideoCard = ({ entry, videoRef }) => (
  <article className="mx-auto flex h-full w-full max-w-[280px] flex-col overflow-hidden rounded-3xl border border-gray-100 bg-white shadow-lg sm:max-w-[300px]">
    <div className="relative aspect-[9/16] w-full bg-black">
      <video
        ref={videoRef}
        src={entry.src}
        className="absolute inset-0 h-full w-full object-contain"
        controls
        playsInline
        preload="metadata"
        aria-label={entry.label}
      />
    </div>
  </article>
);

const HomeRastraPrerna = () => {
  const visibleCount = useVisibleSlideCount();
  const total = rastraVideos.length;
  const maxIndex = Math.max(0, total - visibleCount);
  const [activeIndex, setActiveIndex] = useState(0);
  const videoRefs = useRef([]);

  useEffect(() => {
    setActiveIndex((prev) => Math.min(prev, maxIndex));
  }, [maxIndex]);

  useEffect(() => {
    videoRefs.current.forEach((el, index) => {
      if (!el) return;
      const isVisible =
        index >= activeIndex && index < activeIndex + visibleCount;
      if (!isVisible) {
        el.pause();
      }
    });
  }, [activeIndex, visibleCount]);

  const goTo = useCallback(
    (index) => {
      setActiveIndex(Math.max(0, Math.min(index, maxIndex)));
    },
    [maxIndex],
  );

  const goPrev = () => goTo(activeIndex - 1);
  const goNext = () => goTo(activeIndex + 1);

  const useCarousel = total > visibleCount;
  const trackWidthPercent = (total / visibleCount) * 100;
  const slideShareOfTrack = 100 / total;

  const centeredSlotClass =
    total === 1
      ? 'w-full max-w-[300px]'
      : total === 2
        ? 'w-full max-w-[300px] lg:max-w-[calc((100%-2.5rem)/2)] lg:flex-[1_1_0]'
        : 'w-full max-w-[300px] lg:max-w-[calc((100%-5rem)/3)] lg:flex-[1_1_0]';

  return (
    <section id="rastra-prerna" className="bg-white px-6 py-14 md:px-12">
      <div className="mx-auto max-w-6xl">
        <div className="mb-10 text-center md:mb-12">
          <h2 className="mb-3 text-3xl font-bold uppercase text-accent md:text-4xl">
            RASHTRA PRERNA HIMMATWAALI
          </h2>
          <p className="text-base leading-relaxed text-gray-600 md:text-lg">
            Tribute to Extraordinary Women who Inspire the Nation
          </p>
        </div>

        {useCarousel ? (
          <div className="overflow-hidden">
            <div
              className="flex transition-transform duration-500 ease-out"
              style={{
                width: `${trackWidthPercent}%`,
                transform: `translateX(-${activeIndex * slideShareOfTrack}%)`,
              }}
            >
              {rastraVideos.map((entry, index) => (
                <div
                  key={entry.id}
                  className="box-border flex flex-shrink-0 justify-center px-2 sm:px-3"
                  style={{ width: `${slideShareOfTrack}%` }}
                >
                  <RastraVideoCard
                    entry={entry}
                    videoRef={(el) => {
                      videoRefs.current[index] = el;
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap items-start justify-center gap-6 lg:gap-10">
            {rastraVideos.map((entry, index) => (
              <div key={entry.id} className={centeredSlotClass}>
                <RastraVideoCard
                  entry={entry}
                  videoRef={(el) => {
                    videoRefs.current[index] = el;
                  }}
                />
              </div>
            ))}
          </div>
        )}

        {useCarousel && (
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4 sm:gap-6">
            <button
              type="button"
              onClick={goPrev}
              disabled={activeIndex === 0}
              className="rounded-full border border-gray-200 bg-white p-2.5 text-primary shadow-sm transition hover:bg-bg-light disabled:cursor-not-allowed disabled:opacity-40"
              aria-label="Previous rastra prerna video"
            >
              <ChevronLeft className="h-6 w-6" aria-hidden />
            </button>

            <div
              className="flex flex-wrap justify-center gap-2"
              role="tablist"
              aria-label="Rastra Prerna videos"
            >
              {Array.from({ length: maxIndex + 1 }, (_, pageIndex) => (
                <button
                  key={pageIndex}
                  type="button"
                  role="tab"
                  aria-selected={pageIndex === activeIndex}
                  aria-label={`Show rastra prerna video ${pageIndex + 1}`}
                  onClick={() => goTo(pageIndex)}
                  className={`h-2.5 rounded-full transition-all ${
                    pageIndex === activeIndex
                      ? 'w-8 bg-accent'
                      : 'w-2.5 bg-gray-300 hover:bg-gray-400'
                  }`}
                />
              ))}
            </div>

            <button
              type="button"
              onClick={goNext}
              disabled={activeIndex >= maxIndex}
              className="rounded-full border border-gray-200 bg-white p-2.5 text-primary shadow-sm transition hover:bg-bg-light disabled:cursor-not-allowed disabled:opacity-40"
              aria-label="Next rastra prerna video"
            >
              <ChevronRight className="h-6 w-6" aria-hidden />
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default HomeRastraPrerna;
